import React, { useContext } from 'react';
import { ABContext } from '../../contexts/ABContext';
import { ArrowRightIcon } from 'assests/GmsIcons';
import { TABResult, TModel } from '../../types/ab';
import Status from 'Components/DesignSystem/Status/Status';
import Typography from 'Components/DesignSystem/Typography/Typography';

type TTrafficAllocationItem = {
    model_id: string;
    model_name: string;
    label: string;
    is_control: boolean;
    assigned: number;
    realized: number;
};

/**
 * Assigned → realized traffic per model (`okf/design-system/e2e.md` § 158).
 *
 * **The maths is untouched**: `realized` is still `(sessions / totalSessions) * 100`
 * rounded to one decimal, `totalSessions` is still summed across `testData.result`, the
 * model-config-to-result join is still on `model_id`, control still sorts first, and
 * `formatAssigned` still drops the decimal for a whole number while `formatRealized`
 * always keeps one. Only the markup changed.
 *
 * The inline `style={{ width: `${sortedItems.length * 25}%` }}` stays — it is what
 * keeps a two-model card from stretching to the full width of a wide screen, and there
 * is no utility for a width computed from item count.
 */
const TrafficAllocation = () => {
    const { testData } = useContext(ABContext);

    if (!testData?.applets?.[0]?.models?.length || !testData?.result?.length) return null;

    // Sum session counts across all models from the existing result data
    const totalSessions = testData.result.reduce((sum: number, r: TABResult) => sum + (r.session_count ?? 0), 0);

    // Join model config (assigned %) with result data (session_count) on model_id
    const allocationData: TTrafficAllocationItem[] = testData.applets[0].models.map((model: TModel) => {
        const resultEntry = testData.result.find((r: TABResult) => r.id === model.model_id);
        const sessions = resultEntry?.session_count ?? 0;
        const realized = totalSessions > 0
            ? parseFloat(((sessions / totalSessions) * 100).toFixed(1))
            : 0;
        return {
            model_id: model.model_id,
            model_name: model.model_name,
            label: model.label,
            is_control: model.is_control,
            assigned: model.traffic_distribution,
            realized,
        };
    });

    // Control first, then variants in original order
    const sortedItems = [...allocationData].sort((a, b) => {
        if (a.is_control && !b.is_control) return -1;
        if (!a.is_control && b.is_control) return 1;
        return 0;
    });

    const formatAssigned = (val: number) =>
        Number.isInteger(val) ? `${val}%` : `${val.toFixed(1)}%`;

    const formatRealized = (val: number) => `${val.toFixed(1)}%`;

    return (
        <div className="tw-overflow-x-auto">
        <div className="tw-mt-4 tw-mb-2 tw-border tw-border-edge tw-rounded-srf tw-p-4" style={{ width: `${sortedItems.length * 25}%`, minWidth: 'fit-content' }}>
            <div className="tw-mb-4">
                <Status tone="neutral" label="Traffic Allocation" data-testid="ab-traffic-allocation-badge" />
            </div>

            {/* Model cards — horizontal, divided */}
            <div className="tw-flex tw-divide-x tw-divide-edge">
                {sortedItems.map((item) => {
                    const role = item.is_control ? 'Control' : 'Variant';

                    return (
                        <div key={item.model_id} className="tw-flex-1 tw-px-4 first:tw-pl-0 tw-flex tw-items-center tw-gap-4">
                            {/* Left: Role + Model Name */}
                            <div>
                                <Typography variant="title-header" as="p" className="tw-text-ink tw-leading-snug">
                                    {role}:
                                </Typography>
                                <Typography variant="body-primary" as="p" className="tw-text-ink tw-whitespace-nowrap">
                                    {item.model_name}
                                </Typography>
                            </div>

                            {/*
                              Assigned → Realized. The arrow is decorative: the two
                              captions beneath the numbers already say which is which,
                              and an unlabelled SVG announced as nothing anyway.
                            */}
                            <div className="tw-flex tw-items-start tw-gap-2">
                                <div>
                                    <Typography
                                        variant="heading-2"
                                        as="p"
                                        className="tw-text-ink tw-leading-none tw-tabular-nums"
                                        data-testid={`ab-traffic-assigned-${item.model_id}`}
                                    >
                                        {formatAssigned(item.assigned)}
                                    </Typography>
                                    <Typography variant="body-primary" as="p" className="tw-text-ink-tertiary tw-mt-0.5">
                                        Assigned
                                    </Typography>
                                </div>

                                <span aria-hidden="true" className="tw-mb-4 tw-flex-shrink-0"><ArrowRightIcon /></span>

                                <div>
                                    <Typography
                                        variant="heading-2"
                                        as="p"
                                        className="tw-text-ink tw-leading-none tw-tabular-nums"
                                        data-testid={`ab-traffic-realized-${item.model_id}`}
                                    >
                                        {formatRealized(item.realized)}
                                    </Typography>
                                    <Typography variant="body-primary" as="p" className="tw-text-ink-tertiary tw-mt-0.5">
                                        Realized
                                    </Typography>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
        </div>
    );
};

export default TrafficAllocation;
